import { addDays, format, parseISO } from "date-fns";

type GoogleCalendarEvent = {
  title: string;
  date: string;
  end_date: string | null;
  all_day?: boolean | null;
  description?: string | null;
  provider_name?: string | null;
  contact_name?: string | null;
};

// Compose the Google Calendar details field from the event fields
function buildDetails(event: GoogleCalendarEvent, appUrl?: string) {
  const lines: string[] = [];

  if (event.description) {
    lines.push(stripHtml(event.description));
  }
  if (event.provider_name) {
    lines.push(`Prestataire : ${event.provider_name}`);
  }
  if (event.contact_name) {
    lines.push(`Personnes présentes : ${event.contact_name}`);
  }
  if (appUrl) {
    lines.push("", `Lien : ${appUrl}`);
  }

  return lines.join("\n");
}

// Format a Date as the UTC stamp Google Calendar expects: YYYYMMDDTHHmmssZ
function toUtcStamp(date: Date) {
  return date.toISOString().replace(/[-:]/g, "").replace(/\.\d{3}/, "");
}

// Format a Date as the date-only stamp Google Calendar uses for all-day events: YYYYMMDD
function toDateStamp(date: Date) {
  return format(date, "yyyyMMdd");
}

// Build the `dates` param. All-day events use date-only stamps with an
// exclusive end (Google expects the day after the last day), matching Google's
// own all-day model. Timed events use full UTC timestamps.
function buildDatesParam(event: GoogleCalendarEvent) {
  const start = parseISO(event.date);

  if (event.all_day) {
    // end_date is stored inclusive; Google's all-day end is exclusive, so add a day.
    const lastDay = event.end_date ? parseISO(event.end_date) : start;
    const endExclusive = addDays(lastDay, 1);
    return `${toDateStamp(start)}/${toDateStamp(endExclusive)}`;
  }

  // Default to a one-hour slot when no end date is provided
  const end = event.end_date
    ? parseISO(event.end_date)
    : new Date(start.getTime() + 60 * 60 * 1000);
  return `${toUtcStamp(start)}/${toUtcStamp(end)}`;
}

// Strip HTML tags so descriptions stored as rich text stay readable as plain text
function stripHtml(html: string) {
  return html.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
}

export function getGoogleCalendarUrl(event: GoogleCalendarEvent, appUrl?: string) {
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: event.title,
    dates: buildDatesParam(event),
  });

  const details = buildDetails(event, appUrl);
  if (details) {
    params.set("details", details);
  }

  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}
